import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  Paper,
  Chip,
  Divider,
  Grid,
} from '@mui/material';
import axios from "axios";
import { styled } from '@mui/material/styles';
import DescriptionIcon from '@mui/icons-material/Description';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import MedicationIcon from '@mui/icons-material/Medication';
import BiotechIcon from '@mui/icons-material/Biotech';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { format } from 'date-fns';

const TimelineItem = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginLeft: theme.spacing(3),
  borderRadius: 8,
  backgroundColor: theme.palette.grey[50],
  border: `1px solid ${theme.palette.divider}`,
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    left: -18,
    top: '50%',
    transform: 'translateY(-50%)',
    width: 10,
    height: 10,
    borderRadius: '50%',
    backgroundColor: theme.palette.primary.main,
  }
}));

const TimelineLine = styled(Box)(({ theme }) => ({
  position: 'absolute',
  left: 16,
  top: 0,
  bottom: 0,
  width: 2,
  backgroundColor: theme.palette.divider,
}));

const getCarePlanIcon = (type) => {
  const icons = {
    'clinical-notes': DescriptionIcon,
    'monitoring': MonitorHeartIcon,
    'medication': MedicationIcon,
    'test': BiotechIcon,
    'diet': RestaurantIcon,
    'nutrition': RestaurantIcon,
    'discharge': ExitToAppIcon,
    'task': AssignmentIcon,
  };
  return icons[type] || DescriptionIcon;
};

const getCarePlanLabel = (type) => {
  const labels = {
    'clinical-notes': 'Clinical Notes',
    'monitoring': 'Monitoring',
    'medication': 'Medication',
    'test': 'Test',
    'diet': 'Diet',
    'nutrition': 'Nutrition',
    'discharge': 'Discharge',
    'task': 'Task',
  };
  return labels[type] || (type ? String(type).replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : 'Care Plan');
};

/**
 * Infer care plan type from the data shape when structure_name / care_plan_id
 * lookup is unavailable (e.g. backend does not store or return structure_name).
 */
const inferTypeFromData = (data) => {
  if (!data || typeof data !== 'object') return null;
  if (data.oralDiet && typeof data.oralDiet === 'object') return 'nutrition';
  if (data.medication && typeof data.medication === 'object') return 'medication';
  if (Array.isArray(data.medications)) return 'medication';
  if (data.drugName && typeof data.drugName === 'string') return 'medication';
  if (Array.isArray(data.monitoringItems)) return 'monitoring';
  if (data.parameter && typeof data.parameter === 'string') return 'monitoring';
  if (data.testName || data.orderDetail || Array.isArray(data.tests)) return 'test';
  if (data.test && typeof data.test === 'object') return 'test';
  if (data.dischargePlan) return 'discharge';
  if (data.dietType && typeof data.dietType === 'string') return 'diet';
  if (data.condition && typeof data.condition === 'string') return 'clinical-notes';
  return null;
};

/** Flatten activity-modal shape { structureId, structureName, values } into one data object for timeline display */
const flattenParentCarePlanData = (item) => {
  const d = item?.data && typeof item.data === "object" ? item.data : {};
  if (Object.prototype.hasOwnProperty.call(d, "values") && d.values != null) {
    // Multi-entry forms store values as an array; keep it as-is so consumers can detect it.
    if (Array.isArray(d.values)) {
      return {
        _entries: d.values,
        structureName: d.structureName,
        structureId: d.structureId,
        ...(d.__timeline_group_id ? { __timeline_group_id: d.__timeline_group_id } : {}),
        ...(d.__timeline_created_at ? { __timeline_created_at: d.__timeline_created_at } : {}),
      };
    }
    if (typeof d.values === "object") {
      return {
        ...d.values,
        ...(d.__timeline_group_id ? { __timeline_group_id: d.__timeline_group_id } : {}),
        ...(d.__timeline_created_at ? { __timeline_created_at: d.__timeline_created_at } : {}),
      };
    }
  }
  return { ...d };
};

/** Map in-memory patient.carePlans entries to the same shape as normalizeTimelinePlans (API + UI stay in sync). */
const structureGroupKeyForMerge = (plan) => {
  const cid = Number(
    plan?.carePlanId ??
      plan?.raw?.care_plan_id ??
      plan?.raw?.data?.structureId ??
      plan?.raw?.data?.structure_id ??
      0
  );
  if (Number.isFinite(cid) && cid > 0) return `cid:${cid}`;
  return `row:${plan?.id ?? ""}|${plan?.createdAt ?? ""}|${plan?.sourceIndex ?? ""}`;
};

const getTimelineGroupId = (plan) =>
  plan?.data?.__timeline_group_id ||
  plan?.raw?.data?.__timeline_group_id ||
  null;

const getTimelineCreatedAtMeta = (planOrItem) =>
  planOrItem?.data?.__timeline_created_at ||
  planOrItem?.raw?.data?.__timeline_created_at ||
  null;

const getTimelineSortTsMeta = (planOrItem) => {
  const raw =
    planOrItem?.data?.__timeline_sort_ts ||
    planOrItem?.raw?.data?.__timeline_sort_ts ||
    null;
  if (raw == null || raw === "") return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
};

const parseTimelineTimestamp = (rawValue) => {
  if (!rawValue) return null;
  if (rawValue instanceof Date) {
    const ts = rawValue.getTime();
    return Number.isNaN(ts) ? null : ts;
  }
  const text = String(rawValue).trim();
  if (!text) return null;
  // Handles values like "2026-06-02T09:02:39.206919" safely across browsers.
  // Backend created_at is UTC but often comes without timezone, so treat no-zone
  // timestamps as UTC to keep timeline order and displayed time correct.
  const normalized = text
    .replace(" ", "T")
    .replace(/\.(\d{3})\d+$/, ".$1");
  const hasTimezone = /(?:[zZ]|[+\-]\d{2}:?\d{2})$/.test(normalized);
  const parseCandidate = hasTimezone ? normalized : `${normalized}Z`;
  let ts = new Date(parseCandidate).getTime();
  if (!Number.isNaN(ts)) return ts;
  ts = new Date(text).getTime();
  return Number.isNaN(ts) ? null : ts;
};

const resolveCreatedAtFromApiItem = (item) => {
  const raw =
    item?.created_at ||
    item?.createdAt ||
    item?.creation_date ||
    item?.created_on ||
    item?.created_datetime ||
    item?.created_date_time ||
    item?.date_created ||
    item?.entry_created_at ||
    item?.created_at_utc ||
    item?.inserted_at ||
    item?.time_created ||
    getTimelineCreatedAtMeta(item) ||
    null;
  const ts = parseTimelineTimestamp(raw);
  return {
    createdAt: raw || null,
    createdAtTs: ts,
  };
};

/**
 * Purely additive merge: every API entry is shown as-is. Local (in-session)
 * snapshots are appended when either:
 *   (a) the API has no entry for that structure yet, OR
 *   (b) the local entry is newer than the latest API entry for that structure
 *       (covers the window between a fresh save and the next API fetch).
 * Nothing is removed or overridden.
 */
const mergePreferManualForSameStructure = (apiPlans, localPlans) => {
  if (localPlans.length === 0) return [...apiPlans];

  // Track local batch timestamps so we can prefer local entries when API has
  // the same batch id but no reliable created timestamp yet.
  const localGroupTsByKey = {};
  localPlans.forEach((lp) => {
    const key = structureGroupKeyForMerge(lp);
    const groupId = getTimelineGroupId(lp);
    if (!groupId) return;
    if (!localGroupTsByKey[key]) localGroupTsByKey[key] = {};
    localGroupTsByKey[key][String(groupId)] = lp?.sortTs ?? lp?.createdAtTs ?? null;
  });

  const apiPlansFiltered = apiPlans.filter((ap) => {
    const key = structureGroupKeyForMerge(ap);
    const groupId = getTimelineGroupId(ap);
    if (!groupId) return true;
    const localTs = localGroupTsByKey[key]?.[String(groupId)];
    if (localTs == null) return true;
    const apiSortTs = ap?.sortTs ?? null;
    if (apiSortTs == null) return false;
    const apiTs = apiSortTs ?? ap?.createdAtTs;
    // Prefer local copy for the same batch until API provides save-order metadata.
    return apiTs != null;
  });

  // Build a map: structureKey → latest createdAtTs among all API plans for that key
  const latestApiTsByKey = {};
  const apiGroupIdsByKey = {};
  apiPlansFiltered.forEach((ap) => {
    const key = structureGroupKeyForMerge(ap);
    const ts = ap?.sortTs ?? ap?.createdAtTs ?? 0;
    if (!(key in latestApiTsByKey) || ts > latestApiTsByKey[key]) {
      latestApiTsByKey[key] = ts;
    }
    const groupId = getTimelineGroupId(ap);
    if (groupId) {
      if (!apiGroupIdsByKey[key]) apiGroupIdsByKey[key] = new Set();
      apiGroupIdsByKey[key].add(String(groupId));
    }
  });

  const extras = localPlans.filter((lp) => {
    const key = structureGroupKeyForMerge(lp);
    const groupId = getTimelineGroupId(lp);
    // API already contains this same saved batch; skip local duplicate snapshot.
    if (groupId && apiGroupIdsByKey[key]?.has(String(groupId))) return false;
    // Structure not in API at all — always show the local snapshot
    if (!(key in latestApiTsByKey)) return true;
    const localTs = lp?.sortTs ?? lp?.createdAtTs;
    // Unknown timestamp — err on the side of showing it
    if (localTs == null) return true;
    // Show only if the local plan is newer than the latest API plan for this structure
    return localTs > latestApiTsByKey[key];
  });

  return [...apiPlansFiltered, ...extras];
};

const normalizeParentCarePlansForTimeline = (carePlans, nameByStructureId) => {
  if (!Array.isArray(carePlans)) return [];
  return carePlans.map((item, index) => {
    const flatData = flattenParentCarePlanData(item);
    const d = item?.data && typeof item.data === "object" ? item.data : {};
    const structureName = d.structureName || item?.name || "";
    const carePlanId = d.structureId ?? d.structure_id ?? null;
    const type =
      (item?.type && String(item.type)) ||
      String(structureName || "care-plan")
        .toLowerCase()
        .replace(/\s+/g, "-");
    const createdAt = item.createdAt || d.__timeline_created_at || null;
    const label =
      structureName ||
      (carePlanId != null && nameByStructureId?.[carePlanId]) ||
      null;
    const ts = createdAt ? new Date(createdAt).getTime() : NaN;
    const timelineMetaTs = parseTimelineTimestamp(d.__timeline_created_at);
    const timelineSortTs = Number(d.__timeline_sort_ts);
    return {
      id: item.id || `local-${index}`,
      carePlanId,
      type,
      label,
      data: flatData,
      createdAt,
      createdAtTs: Number.isNaN(ts) ? null : ts,
      sortTs: Number.isFinite(timelineSortTs)
        ? timelineSortTs
        : (timelineMetaTs ?? (Number.isNaN(ts) ? null : ts)),
      sourceIndex: index,
      raw: item,
    };
  });
};

const getDateGroupLabel = (createdAt) => {
  if (!createdAt) return null;
  const itemDate = new Date(createdAt);
  if (Number.isNaN(itemDate.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  const itemDay = new Date(itemDate);
  itemDay.setHours(0, 0, 0, 0);
  if (itemDay.getTime() === today.getTime()) return 'Today';
  if (itemDay.getTime() === yesterday.getTime()) return 'Yesterday';
  return format(itemDay, 'MMM d, yyyy');
};

/** Extract the most specific label available from a plan's data (testName, drugName, etc.). */
const getSpecificPlanLabel = (plan) => {
  const data = plan.data || {};
  const itemName =
    data.testName ||
    data.test_name ||
    data.drugName ||
    data.drug_name ||
    data.parameter ||
    data.taskTitle ||
    data.task_title ||
    data.appointmentType ||
    data.appointment_type ||
    data.noteName ||
    data.title ||
    (data.medication && typeof data.medication === 'object' ? data.medication.name : null) ||
    (Array.isArray(data.tests) && data.tests.length === 1
      ? (data.tests[0]?.testName || data.tests[0]?.name)
      : null) ||
    (Array.isArray(data.medications) && data.medications.length === 1
      ? (data.medications[0]?.drugName || data.medications[0]?.name)
      : null) ||
    (Array.isArray(data.monitoringItems) && data.monitoringItems.length === 1
      ? data.monitoringItems[0]?.parameter
      : null) ||
    null;
  return itemName || plan.label || getCarePlanLabel(plan.type);
};

/** Count the actual clinical items inside a plan's data (medicines, tests, etc.). */
const getItemCount = (plan) => {
  const data = plan.data || {};
  if (Array.isArray(data.medications) && data.medications.length > 0) return data.medications.length;
  if (Array.isArray(data.tests) && data.tests.length > 0) return data.tests.length;
  if (Array.isArray(data.monitoringItems) && data.monitoringItems.length > 0) return data.monitoringItems.length;
  if (Array.isArray(data.tasks) && data.tasks.length > 0) return data.tasks.length;
  if (Array.isArray(data._entries) && data._entries.length > 0) return data._entries.length;
  return 1;
};

const buildGroupedTimeline = (sortedPlans) => {
  const dateGroups = {};
  const dateOrder = [];
  const KNOWN_TYPES = new Set(['medication', 'test', 'monitoring', 'clinical-notes', 'diet', 'nutrition', 'discharge', 'task', 'appointment', 'note']);

  sortedPlans.forEach((plan) => {
    const dateLabel = getDateGroupLabel(plan.createdAt) || 'Today';
    if (!dateGroups[dateLabel]) {
      dateGroups[dateLabel] = {};
      dateOrder.push(dateLabel);
    }
    // Group entries by same care-plan structure (preferred) or by type/label fallback.
    const carePlanKey =
      plan?.carePlanId != null && plan?.carePlanId !== ""
        ? `cid:${String(plan.carePlanId)}`
        : `type:${String(plan?.type || "")}|label:${String(plan?.label || "").toLowerCase()}`;
    const timelineGroupId =
      plan?.data?.__timeline_group_id ||
      plan?.raw?.data?.__timeline_group_id ||
      null;
    const fallbackCreatedAtKey = plan?.createdAt ? `at:${String(plan.createdAt)}` : `id:${String(plan?.id || "")}`;
    // Same save/action should merge into one card, while different saves stay separate.
    const groupKey = timelineGroupId
      ? `${carePlanKey}|tg:${String(timelineGroupId)}`
      : `${carePlanKey}|${fallbackCreatedAtKey}`;
    // For standard care plan types, always use the type label (never drug/item names).
    // For custom dynamic activity types, use the structure name from plan.label.
    const cardLabel = KNOWN_TYPES.has(plan.type)
      ? getCarePlanLabel(plan.type)
      : (plan.label || getCarePlanLabel(plan.type));
    const existingGroup = dateGroups[dateLabel][groupKey];
    if (!existingGroup) {
      dateGroups[dateLabel][groupKey] = {
        type: plan.type,
        label: cardLabel,
        itemCount: getItemCount(plan),
        plans: [plan],
        latestCreatedAt: plan.createdAt,
        latestSortTs: plan.sortTs ?? plan.createdAtTs,
        latestTs: plan.createdAtTs,
        latestSourceIndex: plan.sourceIndex ?? -1,
      };
      return;
    }

    existingGroup.itemCount += getItemCount(plan);
    existingGroup.plans.push(plan);
    const existingTs = existingGroup.latestSortTs ?? existingGroup.latestTs ?? -Infinity;
    const incomingTs = plan.sortTs ?? plan.createdAtTs ?? -Infinity;
    if (incomingTs >= existingTs) {
      existingGroup.latestCreatedAt = plan.createdAt;
      existingGroup.latestSortTs = plan.sortTs ?? plan.createdAtTs;
      existingGroup.latestTs = plan.createdAtTs;
      existingGroup.latestSourceIndex = plan.sourceIndex ?? existingGroup.latestSourceIndex ?? -1;
    }
  });

  // Always show Today first, Yesterday second, then older dates newest-first.
  dateOrder.sort((a, b) => {
    if (a === 'Today') return -1;
    if (b === 'Today') return 1;
    if (a === 'Yesterday') return -1;
    if (b === 'Yesterday') return 1;
    return new Date(b) - new Date(a);
  });

  return dateOrder.map((dateLabel) => ({
    dateLabel,
    groups: Object.values(dateGroups[dateLabel]).sort((a, b) => {
      const ats = a?.latestSortTs ?? a?.latestTs;
      const bts = b?.latestSortTs ?? b?.latestTs;
      if (ats != null && bts != null) return bts - ats;
      if (ats == null && bts == null) {
        return (b?.latestSourceIndex ?? -1) - (a?.latestSourceIndex ?? -1);
      }
      return ats == null ? 1 : -1;
    }),
  }));
};

const formatGroupTime = (group) => {
  if (!group) return null;
  if (group.latestTs != null) {
    return format(new Date(group.latestTs), 'h:mm a');
  }
  if (!group.latestCreatedAt) return null;
  // Through parseTimelineTimestamp like every other timestamp here: a bare
  // new Date() read the unmarked created_at as local and timed the group
  // 5h30m early, disagreeing with the latestTs branch just above.
  const ts = parseTimelineTimestamp(group.latestCreatedAt);
  if (ts == null) return null;
  return format(new Date(ts), 'h:mm a');
};

const toLabel = (value = "") =>
  String(value)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const renderReadableValue = (value) => {
  if (value === null || value === undefined || value === "") {
    return "-";
  }
  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return "-";
    if (value.every((item) => typeof item !== "object")) {
      return value.map((item) => toLabel(item)).join(", ");
    }
    return `${value.length} item(s)`;
  }
  if (typeof value === "object") {
    return "";
  }
  return String(value);
};

const SKIP_FIELDS = new Set([
  "structureId",
  "structureName",
  "structure_id",
  "addToTaskList",
  "__timeline_group_id",
  "__timeline_created_at",
  "__timeline_sort_ts",
]);

const renderField = (key, value, depth = 0, reactKey) => {
  if (depth > 3) {
    return (
      <Grid item xs={12} sm={6} key={reactKey || key}>
        <Typography variant="caption" color="text.secondary">{toLabel(key)}</Typography>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>{String(value ?? "-")}</Typography>
      </Grid>
    );
  }
  const isObj = value && typeof value === "object" && !Array.isArray(value);
  const isArrObjs = Array.isArray(value) && value.length > 0 && value.some((i) => i && typeof i === "object");
  const bgColor = depth === 0 ? "grey.50" : "grey.100";
  return (
    <Grid item xs={12} sm={(isObj || isArrObjs) ? 12 : 6} key={reactKey || key}>
      <Typography variant="caption" color="text.secondary">{toLabel(key)}</Typography>
      {isObj ? (
        <Paper variant="outlined" sx={{ p: 1, mt: 0.5, borderRadius: 1, backgroundColor: bgColor }}>
          <Grid container spacing={1}>
            {Object.entries(value).map(([k, v]) => renderField(k, v, depth + 1))}
          </Grid>
        </Paper>
      ) : isArrObjs ? (
        <Box sx={{ mt: 0.5, display: "flex", flexDirection: "column", gap: 0.75 }}>
          {value.map((item, i) => (
            <Paper key={i} variant="outlined" sx={{ p: 1, borderRadius: 1, backgroundColor: bgColor }}>
              {item && typeof item === "object" ? (
                <Grid container spacing={1}>
                  {Object.entries(item).map(([k, v]) => renderField(k, v, depth + 1))}
                </Grid>
              ) : (
                <Typography variant="body2">{renderReadableValue(item)}</Typography>
              )}
            </Paper>
          ))}
        </Box>
      ) : (
        <Typography variant="body2" sx={{ fontWeight: 500 }}>{renderReadableValue(value)}</Typography>
      )}
    </Grid>
  );
};

const renderActivityDataFields = (rawData) => {
  const data = rawData || {};
  const SKIP = SKIP_FIELDS;
  const hasValuesWrapper = data.values != null && typeof data.values === "object";
  const source = hasValuesWrapper ? data.values : data;
  const entries = Array.isArray(source) ? source : [source];
  const allFields = entries.flatMap((entry, ei) =>
    Object.entries(entry || {}).filter(([k]) => !SKIP.has(k)).map(([k, v]) => ({ k, v, ei }))
  );
  if (allFields.length === 0) {
    return (
      <Grid item xs={12}>
        <Typography variant="body2" color="text.secondary">No activity data available.</Typography>
      </Grid>
    );
  }
  return allFields.map(({ k, v, ei }) => renderField(k, v, 0, `${ei}-${k}`));
};

const PatientTimelineModal = ({
  open,
  onClose,
  admissionId,
  patientName,
  carePlans,
  carePlanStructures,
}) => {
  const [apiPlans, setApiPlans] = useState([]);
  const [isLoadingPlans, setIsLoadingPlans] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);

  const structureNameById = useMemo(() => {
    if (!Array.isArray(carePlanStructures)) return {};
    return carePlanStructures.reduce((acc, item) => {
      const name = item?.name || "";
      if (item?.id != null) acc[item.id] = name;
      // Also index by care_plan_id so API entries (which carry care_plan_id) resolve correctly.
      if (item?.care_plan_id != null && item.care_plan_id !== item?.id) {
        acc[item.care_plan_id] = name;
      }
      return acc;
    }, {});
  }, [carePlanStructures]);
  const CARE_PLAN_API_BASE =
    process.env.REACT_APP_BACKEND_ROUTE_URL || "https://manage-dev.hlthtek.com/app/api/";
  const authToken = sessionStorage.getItem("accessToken") || "";
  const headers = useMemo(
    () => ({
      Authorization: `Bearer ${authToken}`,
      "APP-MODE": sessionStorage.getItem("APP_MODE") || "",
      "ORG-CODE": sessionStorage.getItem("ORG_CODE") || "",
      "USER-ACCESS-KEY": sessionStorage.getItem("USER_ACCESS_KEY") || "",
      USERNAME: sessionStorage.getItem("userName") || "",
    }),
    [authToken]
  );

  const normalizeTimelinePlans = (payload, snbid) => {
    // Comprehensive extraction matching NurseTasksModal.extractList — tries every
    // response envelope the backend is known to produce.
    const tryArr = (v) => (Array.isArray(v) ? v : null);
    const d = payload?.data;
    const list =
      tryArr(d?.care_plans) ||
      tryArr(d?.care_plan_entries) ||
      tryArr(d?.entries) ||
      tryArr(d?.results) ||
      tryArr(d?.care_plan_list) ||
      tryArr(d?.items) ||
      tryArr(d?.data?.care_plans) ||
      tryArr(d?.data) ||
      tryArr(payload?.care_plans) ||
      tryArr(payload?.care_plan_entries) ||
      tryArr(payload?.care_plan_list) ||
      tryArr(payload?.results) ||
      tryArr(payload?.entries) ||
      tryArr(d) ||
      tryArr(payload) ||
      [];
    if (!Array.isArray(list)) return [];
    return list.map((item, index) => {
      const { createdAt, createdAtTs } = resolveCreatedAtFromApiItem(item);
      const timelineMetaTs = parseTimelineTimestamp(getTimelineCreatedAtMeta(item));
      const timelineSortTs = getTimelineSortTsMeta(item);
      return ({
      id:
        item?.care_plan_entry_id ||
        item?.entry_id ||
        (item?.created_at
          ? `${item?.care_plan_id || item?.id || "cp"}-${item.created_at}`
          : `${item?.care_plan_id || item?.id || "cp"}-${index}`),
      carePlanId: item?.care_plan_id || item?.id || null,
      type: (() => {
        const itemData = item?.data || item?.values || item?.payload || {};
        return (
          (item?.type && String(item.type).toLowerCase().replace(/\s+/g, "-")) ||
          item?.structure_name?.toLowerCase?.().replace(/\s+/g, "-") ||
          snbid?.[item?.care_plan_id]?.toLowerCase?.().replace(/\s+/g, "-") ||
          item?.name?.toLowerCase?.().replace(/\s+/g, "-") ||
          inferTypeFromData(itemData) ||
          "care-plan"
        );
      })(),
      label:
        item?.structure_name ||
        snbid?.[item?.care_plan_id] ||
        null,
      data: item?.data || item?.values || item?.payload || {},
      createdAt,
      createdAtTs,
      sortTs: timelineSortTs ?? timelineMetaTs ?? createdAtTs,
      sourceIndex: index,
      raw: item,
    });
    });
  };

  const sortTimelinePlans = (plans = []) =>
    [...plans].sort((a, b) => {
      const ats = a?.sortTs ?? a?.createdAtTs;
      const bts = b?.sortTs ?? b?.createdAtTs;
      // Both have timestamps: newest first
      if (ats != null && bts != null) {
        if (bts !== ats) return bts - ats;
        return (b?.sourceIndex ?? -1) - (a?.sourceIndex ?? -1);
      }
      // Both null: highest sourceIndex first (most recently added to API list)
      if (ats == null && bts == null) return (b?.sourceIndex ?? 0) - (a?.sourceIndex ?? 0);
      // One null: treat null as OLDEST — push below entries that have a real timestamp
      return ats == null ? 1 : -1;
    });
  const resolveAdmissionId = (value) => {
    if (typeof value === "number" && Number.isFinite(value)) return value;
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (!trimmed) return 0;
      const direct = Number(trimmed);
      if (Number.isFinite(direct)) return direct;
      const digits = trimmed.match(/\d+/);
      return digits ? Number(digits[0]) : 0;
    }
    return 0;
  };

  const getDataSignature = (plan) => {
    const data = plan?.data || {};
    return JSON.stringify(data);
  };

  const isSameTimelineRecord = (a, b) => {
    if (!a || !b) return false;
    if (String(a?.id) === String(b?.id)) return true;

    const sameCarePlan = String(a?.carePlanId) === String(b?.carePlanId);
    const sameCreatedAt =
      String(a?.raw?.created_at || a?.createdAt || "") ===
      String(b?.raw?.created_at || b?.createdAt || "");
    const sameData = getDataSignature(a) === getDataSignature(b);

    if (sameCarePlan && sameCreatedAt && sameCreatedAt !== "") return true;
    if (sameCarePlan && sameData) return true;
    return false;
  };

  const parentCarePlansKey = useMemo(() => {
    if (!Array.isArray(carePlans) || carePlans.length === 0) return "";
    return carePlans.map((c) => `${c?.id ?? ""}:${c?.createdAt ?? ""}`).join("|");
  }, [carePlans]);

  useEffect(() => {
    const resolvedAdmissionId = resolveAdmissionId(admissionId);
    if (!open) return;
    if (!resolvedAdmissionId) {
      setApiPlans([]);
      return;
    }

    const controller = new AbortController();
    setIsLoadingPlans(true);

    axios
      .get(
        `${CARE_PLAN_API_BASE}care_plans/?admission_id=${resolvedAdmissionId}`,
        { headers, signal: controller.signal }
      )
      .then((response) => {
        setApiPlans(normalizeTimelinePlans(response?.data, structureNameById));
      })
      .catch((error) => {
        if (axios.isCancel(error) || error?.name === "CanceledError" || error?.code === "ERR_CANCELED") return;
        console.error("Failed to fetch timeline care plans:", error);
        setApiPlans([]);
      })
      .finally(() => {
        setIsLoadingPlans(false);
      });

    return () => {
      controller.abort();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, admissionId, parentCarePlansKey, structureNameById]);

  const localTimelinePlans = useMemo(
    () => normalizeParentCarePlansForTimeline(carePlans, structureNameById),
    [carePlans, structureNameById]
  );

  const mergedPlans = useMemo(
    () => mergePreferManualForSameStructure(apiPlans, localTimelinePlans),
    [apiPlans, localTimelinePlans]
  );

  const sortedPlans = sortTimelinePlans(mergedPlans);
  const groupedTimeline = buildGroupedTimeline(sortedPlans);

  const GroupDetailIcon = selectedGroup ? getCarePlanIcon(selectedGroup.type) : null;

  return (
    <>
      {/* ── Main Timeline Dialog ── */}
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>
          Patient Timeline - {patientName}
        </DialogTitle>
        <DialogContent sx={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {isLoadingPlans ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <Typography color="text.secondary">Loading timeline...</Typography>
            </Box>
          ) : groupedTimeline.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <AccessTimeIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
              <Typography color="text.secondary">No care plans found</Typography>
            </Box>
          ) : (
            <Box sx={{ position: 'relative', py: 2 }}>
              <TimelineLine />
              <Box sx={{ pl: 2 }}>
                {groupedTimeline.map(({ dateLabel, groups }) => (
                  <Box key={dateLabel} sx={{ mb: 1 }}>
                    {/* Date Divider */}
                    <Box sx={{ display: 'flex', alignItems: 'center', my: 1.5, mx: -1 }}>
                      <Box sx={{ flex: 1, height: '1px', backgroundColor: 'divider' }} />
                      <Box
                        sx={{
                          mx: 1.5,
                          px: 1.5,
                          py: 0.4,
                          borderRadius: '12px',
                          backgroundColor: '#e8f0fe',
                          border: '1px solid #c5d8f8',
                          flexShrink: 0,
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{ fontWeight: 600, color: '#1a5276', fontSize: '0.68rem', letterSpacing: 0.3 }}
                        >
                          {dateLabel}
                        </Typography>
                      </Box>
                      <Box sx={{ flex: 1, height: '1px', backgroundColor: 'divider' }} />
                    </Box>

                    {/* Individual cards — one per plan entry */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      {groups.map((group, gi) => {
                        const IconComponent = getCarePlanIcon(group.type);
                        const displayTime = formatGroupTime(group);
                        return (
                          <TimelineItem
                            key={group.label + '|' + dateLabel + '|' + gi}
                            elevation={0}
                            onClick={() => setSelectedGroup(group)}
                            sx={{ cursor: 'pointer' }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <IconComponent sx={{ fontSize: 18, color: 'primary.main', flexShrink: 0 }} />
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {group.label}
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#1a5276', fontWeight: 600 }}>
                                  {group.itemCount} Added
                                </Typography>
                              </Box>
                              {displayTime && (
                                <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                                  {displayTime}
                                </Typography>
                              )}
                            </Box>
                          </TimelineItem>
                        );
                      })}
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Group Detail Dialog ── */}
      <Dialog
        open={Boolean(selectedGroup)}
        onClose={() => setSelectedGroup(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
          {GroupDetailIcon && <GroupDetailIcon sx={{ color: 'primary.main', fontSize: 22 }} />}
          <Box component="span" sx={{ flex: 1 }}>{selectedGroup?.label}</Box>
          <Chip
            label={`${selectedGroup?.plans?.length ?? 0} ${selectedGroup?.plans?.length === 1 ? 'entry' : 'entries'}`}
            size="small"
            sx={{ fontSize: '0.72rem', fontWeight: 600, backgroundColor: '#e8f0fe', color: '#1a5276' }}
          />
        </DialogTitle>
        <DialogContent sx={{ maxHeight: '65vh', overflowY: 'auto' }}>
          {!selectedGroup ? (
            <Typography color="text.secondary">No details available.</Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
              {selectedGroup.plans.map((plan, i) => {
                const raw = plan.raw || {};
                const recordedAt = plan.createdAt || raw?.created_at || raw?.createdAt || null;
                const recordedBy = raw?.created_by?.name || null;
                const specialty = raw?.created_by?.specialty || null;
                return (
                  <Box key={plan.id}>
                    {i > 0 && <Divider sx={{ my: 1.5 }} />}
                    {/* Entry meta */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      {recordedAt ? (
                        <Typography variant="caption" color="text.secondary">
                          {format(new Date(recordedAt), 'MMM d, yyyy h:mm a')}
                        </Typography>
                      ) : (
                        <span />
                      )}
                      {recordedBy && (
                        <Typography variant="caption" color="text.secondary">
                          {recordedBy}{specialty ? ` (${specialty})` : ''}
                        </Typography>
                      )}
                    </Box>
                    {/* Activity data */}
                    <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1.5 }}>
                      <Grid container spacing={1.25}>
                        {renderActivityDataFields(raw?.data || plan.data)}
                      </Grid>
                    </Paper>
                  </Box>
                );
              })}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PatientTimelineModal;
